import { ReactNode, useEffect } from "react";
import isEqual from "lodash/isEqual";
import { cn } from "@/lib/utils";
import { ActivitiesType } from "./model";
import {
  ConversationFilterConfig,
  defaultConfigState,
} from "reducers/conversationInboxReducer";
import {
  Conversation as ConversationType,
  ConversationsFilters,
  Status,
} from "types/conversationInbox";

export const allPresetConfig: PresetConfig = {
  id: ActivitiesType.all,
  label: "Open",
  config: { ...defaultConfigState, status: Status.open },
};

export const configList = (currentUserId: string): PresetConfig[] => [
  allPresetConfig,
  {
    id: ActivitiesType.awaitingReply,
    label: "Awaiting reply",
    config: { ...defaultConfigState, awaitingReply: true },
  },
  {
    id: ActivitiesType.unassigned,
    label: "Unassigned",
    config: { ...defaultConfigState, unassigned: true },
  },
  {
    id: ActivitiesType.assignedToMe,
    label: "Assigned to me",
    config: { ...defaultConfigState, assignedTo: currentUserId },
  },
  {
    id: ActivitiesType.mentioned,
    label: "Internal team mention",
    config: { ...defaultConfigState, isMentioned: true },
  },
  {
    id: ActivitiesType.starred,
    label: "Saved",
    config: { ...defaultConfigState, isStarred: true },
  },
  {
    id: ActivitiesType.closed,
    label: "Closed",
    config: { ...defaultConfigState, status: Status.closed },
  },
];

function checkFilteredChanged(
  type: ActivitiesType,
  currentUserId: string,
  { isStarred, ...configInState }: ConversationFilterConfig
) {
  const optionList = configList(currentUserId);
  const targetConfig =
    optionList.find((option) => option.id === type) || optionList[0];
  const { isStarred: isStarredTarget, ...targetConfigConfig } =
    targetConfig.config;

  return {
    isFilteredChanged: !isEqual(targetConfigConfig, configInState),
    targetConfig: targetConfig.config,
  };
}

interface Props {
  currentUserId: string;
  configInState: ConversationFilterConfig;
  filterType: ActivitiesType;
  isConversationLoading: boolean;
  conversations: ConversationType[];
  counters: ConversationsFilters;
  hasSelectedAccountsAndTypes: boolean;

  onFilterConfigChange(config: ConversationFilterConfig): void;

  onFilterTypeChange(type: ActivitiesType): void;
}

export const ConversationFilters = ({
  currentUserId,
  configInState,
  filterType,
  isConversationLoading,
  conversations,
  counters,
  hasSelectedAccountsAndTypes,
  onFilterConfigChange,
  onFilterTypeChange,
}: Props): JSX.Element => {
  const optionList = configList(currentUserId);
  const { isFilteredChanged, targetConfig } = checkFilteredChanged(
    filterType,
    currentUserId,
    configInState
  );

  useEffect(() => {
    if (!currentUserId) {
      return;
    }

    if (isFilteredChanged) {
      onFilterConfigChange(targetConfig);
      return;
    }
  }, [filterType, isConversationLoading]);

  useEffect(() => {
    /**first time page load, should load conversations of current bucket */
    if (!isFilteredChanged && conversations.length <= 0) {
      const found =
        optionList.find((c) => c.id === filterType) || optionList[0];
      onFilterConfigChange(found.config);
    }
  }, []);

  return (
    <div className="mb-5">
      {optionList.map((menu) => (
        <FilterItem
          count={counters[menu.id]}
          capped={
            counters[`${menu.id}Capped` as keyof ConversationsFilters] as
              boolean | null
          }
          active={filterType === menu.id}
          disabled={!hasSelectedAccountsAndTypes}
          key={menu.id}
          onClick={() => {
            const type = menu.id;
            onFilterTypeChange(type);
          }}
        >
          {menu.label}
        </FilterItem>
      ))}
    </div>
  );
};

export interface PresetConfig {
  id: ActivitiesType;
  label: string;
  config: ConversationFilterConfig;
}

interface FilterItemProps {
  value?: ActivitiesType;
  count: number | null;
  capped?: boolean | null;
  children: ReactNode;
  active: boolean;
  disabled?: boolean;

  onClick(): void;
}

function FilterItem(props: FilterItemProps) {
  const displayCount =
    props.count === null
      ? null
      : props.capped
        ? `${props.count}+`
        : props.count;

  return (
    <div
      className={cn(
        "flex items-center justify-between text-sm leading-[19px] cursor-pointer pb-[18px] transition-[background-color,color,border-color,opacity] duration-150 ease-out",
        props.active
          ? "font-semibold text-base text-blue-500"
          : "font-light text-neutral-400 hover:text-blue-500",
        props.disabled &&
          "text-neutral-400 text-sm cursor-not-allowed pointer-events-none hover:text-neutral-400"
      )}
      onClick={props.onClick}
    >
      <div>{props.children}</div>
      {!props.disabled && (
        <span className="min-w-5 text-center text-sm">{displayCount}</span>
      )}
    </div>
  );
}
